import { LABELS } from './flatfileLabels'
import getMaxCharValidator from '../utils/getMaxCharValidator'
import emptyValueValidator from '../utils/emptyValueValidator'
import getColumnWidth from '../utils/getFlatfileColumnWidth'
import getTPAConstraint from '../utils/getTPAConstraint'
import validateEINRegex from '../utils/validateEIN'
import validatePlanNumber from '../utils/getPlanNumberValidator'
import createNamePrefixValidator from '../utils/getNamePrefixValidator'
import createNotApplicableValidator from '../utils/getNotApplicableValidator'
import validateSSNRegex from '../utils/getSSNValidator'
import { IRA_AMOUNT } from './iraAmount'

export const getInheritedIraParticipantFields = (userAudience) => [
  {
    key: 'recordkeeper',
    label: LABELS.RECORDKEEPER,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'recordKeeper',
        config: {
          noSlashRegex: {
            regex: '\\/|\\\\',
            error: 'Please enter a valid Recordkeeper. No slashes allowed.',
          },
          createMaxCharValidator: getMaxCharValidator(60),
          createEmptyValueValidator: emptyValueValidator('Recordkeeper'),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.RECORDKEEPER),
    },
  },
  {
    key: 'tpa',
    label: LABELS.THIRD_PARTY_ADMINISTRATOR,
    type: 'string',
    constraints: getTPAConstraint(userAudience),
    appearance: {
      size: getColumnWidth(LABELS.THIRD_PARTY_ADMINISTRATOR),
    },
  },
  {
    key: 'planSponsor',
    label: LABELS.PLAN_SPONSOR,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'planSponsor',
        config: {
          noSlashRegex: {
            regex: '\\/|\\\\',
            error: 'Please enter a valid Plan Sponsor. No slashes allowed.',
          },
          createMaxCharValidator: getMaxCharValidator(60),
          createEmptyValueValidator: emptyValueValidator('Plan sponsor'),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.PLAN_SPONSOR),
    },
  },
  {
    key: 'planName',
    label: LABELS.COMPLETE_LEGAL_PLAN_NAME,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'planName',
        config: {
          noSlashRegex: {
            regex: '\\/|\\\\',
            error: 'Please enter a valid Plan Name. No slashes allowed.',
          },
          createMaxCharValidator: getMaxCharValidator(80),
          createEmptyValueValidator: emptyValueValidator(
            'Complete legal plan name'
          ),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.COMPLETE_LEGAL_PLAN_NAME),
    },
  },
  {
    key: 'planEin',
    label: LABELS.EMPLOYER_IDENTIFICATION_NUMBER,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'planEin',
        config: validateEINRegex(),
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.EMPLOYER_IDENTIFICATION_NUMBER),
    },
  },
  {
    key: 'planNumber',
    label: LABELS.THREE_DIGIT_PLAN_NUMBER,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: 'planNumber',
        config: validatePlanNumber(),
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.THREE_DIGIT_PLAN_NUMBER),
    },
  },
  {
    key: 'planIdNumber',
    label: LABELS.PROVIDER_PLAN_ID_NUMBER,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: 'planIdNumber',
        config: {
          createMaxCharValidator: getMaxCharValidator(30),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.PROVIDER_PLAN_ID_NUMBER),
    },
  },
  {
    key: 'firstName',
    label: LABELS.BENEFICIARY_FIRST_NAME,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'firstName',
        config: {
          createMaxCharValidator: getMaxCharValidator(40),
          checkNamePrefix: createNamePrefixValidator(),
          checkNotApplicable: createNotApplicableValidator(),
          createEmptyValueValidator: emptyValueValidator('First name'),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.BENEFICIARY_FIRST_NAME),
    },
  },
  {
    key: 'middleName',
    label: LABELS.BENEFICIARY_MIDDLE_NAME,

    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: 'middleName',
        config: {
          createMaxCharValidator: getMaxCharValidator(20),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.BENEFICIARY_MIDDLE_NAME),
    },
  },
  {
    key: 'lastName',
    label: LABELS.BENEFICIARY_LAST_NAME,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'lastName',
        config: {
          createMaxCharValidator: getMaxCharValidator(60),
          checkNotApplicable: createNotApplicableValidator(),
          createEmptyValueValidator: emptyValueValidator('Last name'),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.BENEFICIARY_LAST_NAME),
    },
  },
  {
    key: 'addressLine1',
    label: LABELS.BENEFICIARY_ADDRESS_LINE_1,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'addressLine1',
        config: {
          createMaxCharValidator: getMaxCharValidator(36),
          checkNotApplicable: createNotApplicableValidator(),
          createEmptyValueValidator: emptyValueValidator('Address line 1'),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.BENEFICIARY_ADDRESS_LINE_1),
    },
  },
  {
    key: 'addressLine2',
    label: LABELS.BENEFICIARY_ADDRESS_LINE_2,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: 'addressLine2',
        config: {
          createMaxCharValidator: getMaxCharValidator(36),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.BENEFICIARY_ADDRESS_LINE_2),
    },
  },
  {
    key: 'city',
    label: LABELS.BENEFICIARY_CITY,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: 'city',
        config: {
          createMaxCharValidator: getMaxCharValidator(22),
          createEmptyValueValidator: emptyValueValidator('City'),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.BENEFICIARY_CITY),
    },
  },
  {
    key: 'state',
    label: LABELS.BENEFICIARY_STATE,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: 'state',
        config: {
          createMaxCharValidator: getMaxCharValidator(22),
          createEmptyValueValidator: emptyValueValidator('State'),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.BENEFICIARY_STATE),
    },
  },
  {
    key: 'zipCode',
    label: LABELS.BENEFICIARY_ZIP_CODE,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: 'zipCode',
        config: {
          createMaxCharValidator: getMaxCharValidator(10),
          createEmptyValueValidator: emptyValueValidator('Zip code'),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.BENEFICIARY_ZIP_CODE),
    },
  },
  {
    key: 'countryCode',
    label: LABELS.BENEFICIARY_COUNTRY_CODE,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'countryCode',
        config: {
          createEmptyValueValidator: emptyValueValidator('Country code'),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.BENEFICIARY_COUNTRY_CODE),
    },
  },
  {
    key: 'dob',
    label: LABELS.BENEFICIARY_DATE_OF_BIRTH,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.BENEFICIARY_DATE_OF_BIRTH),
    },
  },
  {
    key: 'ssn',
    label: LABELS.BENEFICIARY_SSN,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'ssn',
        config: {
          ssnValidityCheck: validateSSNRegex(),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.BENEFICIARY_SSN),
    },
  },
  {
    key: 'emailAddress',
    label: LABELS.BENEFICIARY_EMAIL_ADDRESS,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: 'emailAddress',
        config: {
          createMaxCharValidator: getMaxCharValidator(50),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.BENEFICIARY_EMAIL_ADDRESS),
    },
  },
  {
    key: 'traditionalIraPreTaxAmount',
    label: LABELS.TRADITIONAL_IRA_PRE_TAX_AMOUNT,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: 'traditionalIraPreTaxAmount',
        config: {
          createMaxCharValidator: getMaxCharValidator(14),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.TRADITIONAL_IRA_PRE_TAX_AMOUNT),
    },
  },
  {
    key: 'traditionalIraAfterTaxAmount',
    label: LABELS.TRADITIONAL_IRA_AFTER_TAX_AMOUNT,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: 'traditionalIraAfterTaxAmount',
        config: {
          createMaxCharValidator: getMaxCharValidator(14),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.TRADITIONAL_IRA_AFTER_TAX_AMOUNT),
    },
  },
  {
    key: IRA_AMOUNT.ROTH,
    label: LABELS.ROTH_IRA_AMOUNT,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: 'rothIraAmount',
        config: {
          createMaxCharValidator: getMaxCharValidator(14),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.ROTH_IRA_AMOUNT),
    },
  },
  {
    key: 'doAmtsIncludeRMDAmt',
    label: LABELS.INCLUDE_RMD_AMT,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'doAmtsIncludeRMDAmt',
        config: {
          includeRmdAmt: {
            regex: '^(?:N)$',
            error: `Must be ‘N’ for no`,
          },
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.INCLUDE_RMD_AMT),
    },
  },
  {
    key: 'decedentFirstName',
    label: LABELS.DECEDENT_FIRST_NAME,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'decedentFirstName',
        config: {
          createMaxCharValidator: getMaxCharValidator(40),
          checkNamePrefix: createNamePrefixValidator(),
          checkNotApplicable: createNotApplicableValidator(),
          createEmptyValueValidator: emptyValueValidator('Decedent first name'),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.DECEDENT_FIRST_NAME),
    },
  },
  {
    key: 'decedentMiddleName',
    label: LABELS.DECEDENT_MIDDLE_NAME,
    type: 'string',
    constraints: [
      {
        type: 'external',
        validator: 'decedentMiddleName',
        config: {
          createMaxCharValidator: getMaxCharValidator(20),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.DECEDENT_MIDDLE_NAME),
    },
  },
  {
    key: 'decedentLastName',
    label: LABELS.DECEDENT_LAST_NAME,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
      {
        type: 'external',
        validator: 'lastName',
        config: {
          createMaxCharValidator: getMaxCharValidator(60),
          checkNotApplicable: createNotApplicableValidator(),
          createEmptyValueValidator: emptyValueValidator('Decedent last name'),
        },
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.DECEDENT_LAST_NAME),
    },
  },
  {
    key: 'decedentDateOfBirth',
    label: LABELS.DECEDENT_DOB,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.DECEDENT_DOB),
    },
  },
  {
    key: 'decedentDateOfDeath',
    label: LABELS.DECEDENT_DOD,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.DECEDENT_DOD),
    },
  },
  {
    key: 'relationshipToBeneficiary',
    label: LABELS.RELATIONSHIP_TO_BENEFICIARY,
    type: 'string',
    constraints: [
      {
        type: 'required',
      },
    ],
    appearance: {
      size: getColumnWidth(LABELS.RELATIONSHIP_TO_BENEFICIARY),
    },
  },
]
